import { useState } from 'react';
import { supabasePulse } from '../lib/supabasePulse';
import { useNavigate } from 'react-router';
import { Lock, Loader2, AlertCircle } from 'lucide-react';
import { isPulseEmailVerified } from '../lib/emailVerification';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data, error } = await supabasePulse.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.session) {
        if (!isPulseEmailVerified(data.session.user)) {
          await supabasePulse.auth.signOut();
          throw new Error('Please verify your email before signing in.');
        }

        navigate('/');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to sign in';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-theme-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-theme-panel border border-theme-border rounded-2xl p-8 shadow-2xl">
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 bg-theme-cta/10 rounded-xl flex items-center justify-center mb-4">
            <Lock className="w-6 h-6 text-theme-cta" />
          </div>
          <h1 className="text-2xl font-black text-theme-text tracking-tight">Pulse Login</h1>
          <p className="text-theme-text-muted text-sm">Authorized Personnel Only</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-theme-error-bg border border-theme-error/20 rounded-xl flex items-start gap-3 text-theme-error text-sm">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <p>{error}</p>
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-theme-text-muted uppercase tracking-wider mb-1.5">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-theme-input-bg border border-theme-input-border rounded-lg px-4 py-3 text-theme-input-text focus:ring-2 focus:ring-theme-cta/20 focus:border-theme-cta outline-none transition-all placeholder:text-theme-input-placeholder"
              placeholder="name@example.com"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-theme-text-muted uppercase tracking-wider mb-1.5">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-theme-input-bg border border-theme-input-border rounded-lg px-4 py-3 text-theme-input-text focus:ring-2 focus:ring-theme-cta/20 focus:border-theme-cta outline-none transition-all placeholder:text-theme-input-placeholder"
              placeholder="••••••••"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-theme-cta hover:bg-theme-cta-hover text-theme-cta-text font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Access Dashboard'}
          </button>
        </form>
      </div>
    </div>
  );
}
